
import { useState, useCallback, useEffect, useRef } from 'react';

interface FullscreenOptions {
  onEnter?: () => void;
  onExit?: () => void;
  onError?: (error: Error) => void;
}

export const useFullscreen = (options: FullscreenOptions = {}) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const elementRef = useRef<HTMLElement | null>(null);
  const { onEnter, onExit, onError } = options;

  // Check if fullscreen API is supported
  const isSupported = useCallback(() => {
    return !!(
      document.fullscreenEnabled ||
      (document as any).webkitFullscreenEnabled ||
      (document as any).mozFullScreenEnabled ||
      (document as any).msFullscreenEnabled
    );
  }, []);

  // Get current fullscreen element
  const getFullscreenElement = useCallback(() => {
    return (
      document.fullscreenElement ||
      (document as any).webkitFullscreenElement ||
      (document as any).mozFullScreenElement ||
      (document as any).msFullscreenElement
    );
  }, []);

  // Enter fullscreen with comprehensive browser support
  const enterFullscreen = useCallback(async (element?: HTMLElement) => {
    try {
      const targetElement = element || elementRef.current;
      if (!targetElement || getFullscreenElement()) {
        return false;
      }

      let fullscreenPromise: Promise<void> | undefined;

      if (targetElement.requestFullscreen) {
        fullscreenPromise = targetElement.requestFullscreen();
      } else if ((targetElement as any).webkitRequestFullscreen) {
        fullscreenPromise = (targetElement as any).webkitRequestFullscreen();
      } else if ((targetElement as any).mozRequestFullScreen) {
        fullscreenPromise = (targetElement as any).mozRequestFullScreen();
      } else if ((targetElement as any).msRequestFullscreen) {
        fullscreenPromise = (targetElement as any).msRequestFullscreen();
      }

      if (fullscreenPromise) {
        await fullscreenPromise;
        console.log('Successfully entered fullscreen mode');
        return true;
      } else {
        throw new Error('Fullscreen API not supported');
      }
    } catch (error) {
      console.warn('Failed to enter fullscreen:', error);
      onError?.(error as Error);
      return false;
    }
  }, [getFullscreenElement, onError]);

  // Exit fullscreen with comprehensive browser support
  const exitFullscreen = useCallback(async () => {
    try {
      if (!getFullscreenElement()) {
        return false;
      }

      let exitPromise: Promise<void> | undefined;

      if (document.exitFullscreen) {
        exitPromise = document.exitFullscreen();
      } else if ((document as any).webkitExitFullscreen) {
        exitPromise = (document as any).webkitExitFullscreen();
      } else if ((document as any).mozCancelFullScreen) {
        exitPromise = (document as any).mozCancelFullScreen();
      } else if ((document as any).msExitFullscreen) {
        exitPromise = (document as any).msExitFullscreen();
      }

      if (exitPromise) {
        await exitPromise;
        console.log('Successfully exited fullscreen mode');
        return true;
      } else {
        throw new Error('Exit fullscreen API not supported');
      }
    } catch (error) {
      console.warn('Failed to exit fullscreen:', error);
      onError?.(error as Error);
      return false;
    }
  }, [getFullscreenElement, onError]);

  // Toggle fullscreen
  const toggleFullscreen = useCallback(async (element?: HTMLElement) => {
    if (getFullscreenElement()) {
      return await exitFullscreen();
    } else {
      return await enterFullscreen(element);
    }
  }, [getFullscreenElement, enterFullscreen, exitFullscreen]);

  // Handle fullscreen change events
  useEffect(() => {
    const handleFullscreenChange = () => {
      const isCurrentlyFullscreen = !!getFullscreenElement();
      setIsFullscreen(isCurrentlyFullscreen);
      
      if (isCurrentlyFullscreen) {
        onEnter?.();
      } else {
        onExit?.();
      }
    };

    // Add event listeners for all browser variants
    const events = [
      'fullscreenchange',
      'webkitfullscreenchange',
      'mozfullscreenchange',
      'MSFullscreenChange'
    ];

    events.forEach(event => {
      document.addEventListener(event, handleFullscreenChange);
    });

    // Initial check
    handleFullscreenChange();

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleFullscreenChange);
      });
    };
  }, [getFullscreenElement, onEnter, onExit]);

  return {
    isFullscreen,
    isSupported: isSupported(),
    enterFullscreen,
    exitFullscreen,
    toggleFullscreen,
    elementRef
  };
};
